"use client";

import { memo, useCallback, useMemo } from "react";
import { useController, type Control } from "react-hook-form";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { AttributeIcon } from "@/components/main-services/hotels/detail/rooms/AttributeIcon";
import { useHotelFilterCriteria } from "@/hooks/hotel/useHotel";
import {
  type BookForMeFormData,
  extractFacilityAttributes,
} from "../book-for-me.type";

interface BookForMePreferenceFieldProps {
  control: Control<BookForMeFormData>;
}

/**
 * Multi-select preference chips sourced from Hotel Filter Criteria (Facilities only).
 */
const BookForMePreferenceField = ({
  control,
}: BookForMePreferenceFieldProps) => {
  const {
    field: { value, onChange },
  } = useController({ control, name: "preferences" });

  const { data, isLoading } = useHotelFilterCriteria();

  const facilities = useMemo(
    () => extractFacilityAttributes(data?.data),
    [data?.data],
  );

  const handleToggle = useCallback(
    (id: string) => {
      const selected = value ?? [];
      onChange(
        selected.includes(id)
          ? selected.filter((item) => item !== id)
          : [...selected, id],
      );
    },
    [value, onChange],
  );

  if (isLoading) {
    return (
      <div className="flex flex-wrap gap-2.5 pt-1">
        {Array.from({ length: 6 }).map((_, index) => (
          <Skeleton
            key={`pref-skeleton-${index}`}
            className="h-9 w-28 rounded-full"
          />
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-2.5 pt-1">
      {facilities.map((pref) => {
        const prefId = String(pref.id);
        const isChecked = value?.includes(prefId) ?? false;

        return (
          <button
            key={prefId}
            type="button"
            aria-pressed={isChecked}
            onClick={() => handleToggle(prefId)}
            className={cn(
              "inline-flex items-center gap-2 rounded-full border px-3.5 py-2 text-xs font-medium text-body transition-all md:text-sm",
              isChecked
                ? "bg-primary"
                : "bg-white hover:border-border hover:bg-surface-sunken",
            )}
          >
            <AttributeIcon
              attribute={pref as never}
              className="size-4 text-body"
            />
            <span>{pref.name}</span>
          </button>
        );
      })}
    </div>
  );
};

export default memo(BookForMePreferenceField);
